import type { WorldGraphEdge, WorldGraphKind, WorldGraphNode, WorldGraphSnapshot } from "./world-graph-types";

/** Presentation only: coordinates and names never change the saved graph. */
export type WorldModelCanvasIcon = "system" | "globe" | "bank" | "company" | "person" | "news" | "science" | "market" | "source" | "metric" | "claim" | "event" | "evidence" | "idea" | "forecast" | "outcome" | "portfolio" | "simulation";
export type WorldModelCanvasScope = "all" | "sources" | "systems" | "beliefs" | "timeline" | "simulations" | "portfolio";
export type WorldModelCanvasDescription = { title: string; kindLabel: string; icon: WorldModelCanvasIcon };
export type WorldModelCanvasCard = WorldModelCanvasDescription & {
  id: string; node: WorldGraphNode; x: number; y: number; width: number; height: number;
  degree: number; clusterId: string;
};
export type WorldModelCanvasLayout = {
  mode: "overview" | "focus"; focusId: string | null; scope: WorldModelCanvasScope;
  cards: WorldModelCanvasCard[]; edges: WorldGraphEdge[];
  /** Scope count excludes the retained focus; overview count includes every matching saved record. */
  totalInScope: number; hiddenCount: number; hiddenEdgeCount: number;
  page: number; pageCount: number;
  bounds: { x: number; y: number; width: number; height: number };
};
export type WorldModelCanvasOptions = { focusId?: string | null; scope?: WorldModelCanvasScope; limit?: number; page?: number };

export const WORLD_MODEL_CANVAS_CARD_WIDTH = 256;
export const WORLD_MODEL_CANVAS_CARD_HEIGHT = 88;
const CARD_GAP_X = 66;
const CARD_GAP_Y = 80;
const MAX_VISIBLE_CARDS = 80;
const MAX_VISIBLE_EDGES = 160;
const scopeKinds: Record<Exclude<WorldModelCanvasScope, "all">, readonly WorldGraphKind[]> = {
  sources: ["source", "surface"], systems: ["system", "variable"], beliefs: ["hypothesis"],
  timeline: ["event", "claim", "evidence", "outcome"],
  simulations: ["simulation", "projection", "forecast"], portfolio: ["position"],
};
const kindDescriptions: Record<WorldGraphKind, { kindLabel: string; icon: WorldModelCanvasIcon }> = {
  source: { kindLabel: "Source", icon: "source" }, surface: { kindLabel: "Channel", icon: "news" },
  system: { kindLabel: "System", icon: "system" }, variable: { kindLabel: "Indicator", icon: "metric" },
  claim: { kindLabel: "Claim", icon: "claim" }, event: { kindLabel: "Event", icon: "event" },
  evidence: { kindLabel: "Evidence", icon: "evidence" }, hypothesis: { kindLabel: "Hypothesis", icon: "idea" },
  forecast: { kindLabel: "Forecast", icon: "forecast" }, outcome: { kindLabel: "Outcome", icon: "outcome" },
  position: { kindLabel: "Holding", icon: "portfolio" }, simulation: { kindLabel: "Simulation", icon: "simulation" },
  projection: { kindLabel: "Projection", icon: "forecast" },
};

function stringAttribute(node: WorldGraphNode, name: string) {
  return typeof node.attributes[name] === "string" ? node.attributes[name].trim().toLowerCase() : "";
}

/** Icons use saved types, never guesses from a person's name or a claim's wording. */
export function describeWorldModelNode(node: WorldGraphNode): WorldModelCanvasDescription {
  const topicTitles: Record<string, string> = {
    "system:ai-development-deployment": "AI & technology",
    "system:strategic-interaction": "Global relations",
    "system:security-defense": "Security & defense",
  };
  const title = (node.kind === "system" ? topicTitles[node.id] : undefined) ?? (node.label.replace(/\s+/g, " ").trim() || "Untitled record");
  const description = { title, ...kindDescriptions[node.kind] };
  if (node.attributes.epistemicClass === "indexed-publication") return { ...description, kindLabel: "Publication" };
  if (node.attributes.epistemicClass === "hypothesis-variable") return { ...description, kindLabel: "Variable" };
  if (node.attributes.epistemicClass === "proposed-test") return { ...description, kindLabel: "Test proposal" };
  if (node.attributes.epistemicClass === "public-author-belief") return { ...description, kindLabel: "Attributed belief" };
  if (node.attributes.epistemicClass === "paper-forward-forecast") return { ...description, kindLabel: "Paper forecast" };
  if (node.kind === "position") {
    return { ...description, kindLabel: node.attributes.paper === true ? "Paper holding" : "Portfolio record" };
  }
  if (node.kind !== "source") return description;
  const category = stringAttribute(node, "category");
  const sourceKind = stringAttribute(node, "sourceKind");
  if (category === "person" || sourceKind === "human source") return { title, kindLabel: "Person", icon: "person" };
  if (category === "market" || /\b(exchange|brokerage|prediction market)\b/.test(sourceKind)) return { title, kindLabel: "Market", icon: "market" };
  if (category === "media") return { title, kindLabel: "Media", icon: "news" };
  if (/\b(bank|central bank)\b/.test(sourceKind)) return { title, kindLabel: "Bank", icon: "bank" };
  if (/\b(government|regulator)\b/.test(sourceKind)) return { title, kindLabel: "Public institution", icon: "bank" };
  if (/\b(company|issuer)\b/.test(sourceKind)) return { title, kindLabel: "Company", icon: "company" };
  if (/\b(research|university|scientific)\b/.test(sourceKind)) return { title, kindLabel: "Research", icon: "science" };
  if (category === "institution") return { title, kindLabel: "Institution", icon: "company" };
  return description;
}

/** Preserve the original edge for inspection; this is only its short canvas label. */
export function worldModelConnectionLabel(edge: WorldGraphEdge): string {
  const labels: Record<string, string> = {
    proposes_relationship_with: "Proposed link", candidate_market_context: "Market context",
    contextual_system_link: "Context for", registered_participant: "Participates in", participates_in: "Participates in",
    has_variable: "Tracks", models_variable: "Models", role_at: "Role at", source_for: "Source for",
    published_by: "Published by", supports: "Supports", challenges: "Challenges", contradicts: "Contradicts",
    cites: "Cites", has_evidence: "Has evidence", derived_from: "Draws from", held_at: "Held at",
    projects: "Projects", tests: "Tests", observes: "Observes", about: "About", influences: "May influence",
    attributed_speaker: "Attributed speaker", authors: "Authors", cites_dossier_evidence: "Cites evidence",
    contains_scenario: "Includes scenario", contains_system: "Includes system", context_for_projection: "Context for projection",
    context_for_simulation: "Context for simulation", contextual_market_match: "Related market", contributes_to: "Contributes to",
    declared_simulation_input: "Declared input", derived_from_claim: "Draws from claim", derived_from_publication: "Draws from publication",
    dossier_system_context: "Related system", extracted_from: "Extracted from", founded: "Founded",
    has_documented_economic_tie: "Economic link", has_documented_role: "Documented role", has_dossier_asset_mark: "Recorded asset value",
    has_flow: "Tracks flow", has_incentive_dossier: "Incentive profile", has_incentive_hypothesis: "Possible incentive",
    has_indicator: "Tracks", has_state: "Tracks state", has_stock: "Tracks stock", hosts: "Hosts",
    incentive_target_context: "Related interest", leads: "Leads", operates: "Operates",
    packet_projection_reference: "References projection", packet_simulation_system_context: "Simulation context",
    position_source_reference: "Account source", produces_projection: "Produces projection", projects_system: "Projects system",
    proposes_feedback: "Possible feedback", proposes_mechanism: "Proposed mechanism", published: "Published", publishes: "Publishes",
    quoted_from: "Quoted from", records_projection_reference: "References projection", records_simulation_reference: "References simulation",
    registered_source_for: "Source for", source_actor_exposure_to: "Recorded exposure", timeline_reference: "Timeline reference",
    timeline_system_context: "Related system", verification_due_for: "Due for review",
  };
  return labels[edge.relation] ?? edge.relation.replace(/[_-]+/g, " ").replace(/^./, (letter) => letter.toUpperCase());
}

function inScope(node: WorldGraphNode, scope: WorldModelCanvasScope) {
  return scope === "all" || scopeKinds[scope].includes(node.kind);
}
function compareText(a: string, b: string) { return a < b ? -1 : a > b ? 1 : 0; }
function nodePriority(node: WorldGraphNode) {
  if (node.kind === "position") return 0;
  if (node.kind === "system") return 1;
  if (node.kind === "source") return stringAttribute(node, "category") === "institution" ? 2 : 3;
  return ({ surface: 4, variable: 5, hypothesis: 6, forecast: 7, projection: 8, simulation: 9, event: 10, claim: 11, evidence: 12, outcome: 13 } as Partial<Record<WorldGraphKind, number>>)[node.kind] ?? 14;
}
function compareNodes(a: WorldGraphNode, b: WorldGraphNode) {
  return nodePriority(a) - nodePriority(b) || compareText(a.label.toLowerCase(), b.label.toLowerCase()) || compareText(a.id, b.id);
}
function integer(value: number | undefined, fallback: number, min: number, max: number) {
  return value !== undefined && Number.isFinite(value) ? Math.min(max, Math.max(min, Math.floor(value))) : fallback;
}

function graphIndex(snapshot: WorldGraphSnapshot) {
  const nodes = new Map(snapshot.nodes.map((node) => [node.id, node]));
  const neighbors = new Map<string, Set<string>>();
  const edges = snapshot.edges.filter((edge) => nodes.has(edge.from) && nodes.has(edge.to));
  for (const edge of edges) {
    if (edge.from === edge.to) continue;
    if (!neighbors.has(edge.from)) neighbors.set(edge.from, new Set());
    if (!neighbors.has(edge.to)) neighbors.set(edge.to, new Set());
    neighbors.get(edge.from)!.add(edge.to);
    neighbors.get(edge.to)!.add(edge.from);
  }
  return { nodes, neighbors, edges };
}

/** A sparse overview of actual systems; focusing reveals one saved neighborhood at a time. */
export function buildWorldModelCanvas(snapshot: WorldGraphSnapshot, options: WorldModelCanvasOptions = {}): WorldModelCanvasLayout {
  const scope = options.scope ?? "all";
  const index = graphIndex(snapshot);
  const focus = options.focusId ? index.nodes.get(options.focusId) : undefined;
  const limit = integer(options.limit, focus ? 10 : 18, 2, MAX_VISIBLE_CARDS);
  const cards: WorldModelCanvasCard[] = [];
  const addCard = (node: WorldGraphNode, x: number, y: number, clusterId = node.id) => {
    cards.push({ id: node.id, node, ...describeWorldModelNode(node), x, y,
      width: WORLD_MODEL_CANVAS_CARD_WIDTH, height: WORLD_MODEL_CANVAS_CARD_HEIGHT,
      degree: index.neighbors.get(node.id)?.size ?? 0, clusterId });
  };
  let totalInScope = 0;
  let page = 0;
  let pageCount = 1;
  if (focus) {
    const sourceRelations = new Map<string, number>();
    if (focus.kind === "source" || focus.kind === "surface") for (const edge of index.edges) {
      if (edge.from !== focus.id && edge.to !== focus.id) continue;
      const neighbor = index.nodes.get(edge.from === focus.id ? edge.to : edge.from)!;
      const namedRecord = neighbor.kind === "source" || neighbor.kind === "surface";
      const rank = namedRecord && ["publishes", "authors", "hosts", "operates"].includes(edge.relation) ? 0
        : namedRecord && ["leads", "founded"].includes(edge.relation) ? 1
          : namedRecord ? 2 : neighbor.kind === "system" ? 3 : 4;
      sourceRelations.set(neighbor.id, Math.min(sourceRelations.get(neighbor.id) ?? Infinity, rank));
    }
    const neighbors = [...(index.neighbors.get(focus.id) ?? [])].map((id) => index.nodes.get(id)!).filter((node) => inScope(node, scope))
      .sort((a, b) => (sourceRelations.get(a.id) ?? 0) - (sourceRelations.get(b.id) ?? 0) || compareNodes(a, b));
    totalInScope = neighbors.length;
    pageCount = Math.max(1, Math.ceil(neighbors.length / (limit - 1)));
    page = integer(options.page, 0, 0, pageCount - 1);
    const visible = neighbors.slice(page * (limit - 1), (page + 1) * (limit - 1));
    addCard(focus, 0, 0);
    // Occupy the nearest unoccupied grid cells first. Cards never overlap, even at the maximum limit.
    const radius = Math.ceil(Math.sqrt(visible.length + 1));
    const slots: { column: number; row: number }[] = [];
    for (let row = -radius; row <= radius; row++) for (let column = -radius; column <= radius; column++) {
      if (column || row) slots.push({ column, row });
    }
    slots.sort((a, b) => (a.column * a.column + a.row * a.row) - (b.column * b.column + b.row * b.row) || a.row - b.row || a.column - b.column);
    visible.forEach((node, i) => addCard(node, slots[i].column * (WORLD_MODEL_CANVAS_CARD_WIDTH + CARD_GAP_X), slots[i].row * (WORLD_MODEL_CANVAS_CARD_HEIGHT + CARD_GAP_Y), focus.id));
  } else {
    const candidates = [...index.nodes.values()].filter((node) => inScope(node, scope)).sort(compareNodes);
    totalInScope = candidates.length;
    const systems = candidates.filter((node) => node.kind === "system");
    // The world overview is a readable map of saved topics. Their records open on focus,
    // while each layer and global search can reach every individual saved record.
    const overviewNodes = scope === "all" && systems.length ? systems : candidates;
    totalInScope = overviewNodes.length;
    pageCount = Math.max(1, Math.ceil(overviewNodes.length / limit));
    page = integer(options.page, 0, 0, pageCount - 1);
    const visible = overviewNodes.slice(page * limit, (page + 1) * limit);
    const columns = Math.max(1, Math.min(3, visible.length));
    visible.forEach((node, i) => addCard(node,
      (i % columns) * (WORLD_MODEL_CANVAS_CARD_WIDTH + 100),
      Math.floor(i / columns) * (WORLD_MODEL_CANVAS_CARD_HEIGHT + 90)));
  }
  const visibleIds = new Set(cards.map((card) => card.id));
  const visibleEdges = index.edges.filter((edge) => visibleIds.has(edge.from) && visibleIds.has(edge.to));
  visibleEdges.sort((a, b) => Number(b.from === focus?.id || b.to === focus?.id) - Number(a.from === focus?.id || a.to === focus?.id) || compareText(a.id, b.id));
  const minX = cards.length ? Math.min(...cards.map((card) => card.x)) : 0;
  const minY = cards.length ? Math.min(...cards.map((card) => card.y)) : 0;
  const maxX = cards.length ? Math.max(...cards.map((card) => card.x + card.width)) : 0;
  const maxY = cards.length ? Math.max(...cards.map((card) => card.y + card.height)) : 0;
  return {
    mode: focus ? "focus" : "overview", focusId: focus?.id ?? null, scope, cards,
    edges: visibleEdges.slice(0, MAX_VISIBLE_EDGES), totalInScope,
    hiddenCount: Math.max(0, totalInScope - cards.length + (focus ? 1 : 0)), hiddenEdgeCount: Math.max(0, visibleEdges.length - MAX_VISIBLE_EDGES),
    page, pageCount, bounds: { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
  };
}

/** Search is always global, regardless of canvas focus, scope, or the visible-card limit. */
export function searchWorldModelCanvas(snapshot: WorldGraphSnapshot, query: string, options: { limit?: number; offset?: number } = {}) {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const result = snapshot.nodes.filter((node) => {
    const description = describeWorldModelNode(node);
    const haystack = [node.id, node.label, description.title, description.kindLabel, node.kind, node.summary, node.status,
      ...node.sourceIds, ...node.provenance.map((entry) => entry.label), ...Object.values(node.attributes).map(String)].join(" ").toLowerCase();
    return words.every((word) => haystack.includes(word));
  }).sort((a, b) => {
    const term = query.trim().toLowerCase();
    const score = (node: WorldGraphNode) => node.id.toLowerCase() === term || node.label.toLowerCase() === term ? 0 : node.label.toLowerCase().startsWith(term) ? 1 : 2;
    return score(a) - score(b) || compareNodes(a, b);
  });
  const limit = integer(options.limit, 20, 1, 100);
  const offset = integer(options.offset, 0, 0, result.length);
  return { results: result.slice(offset, offset + limit), total: result.length, offset, hasMore: offset + limit < result.length };
}
